import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, X, Plus } from "lucide-react";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AcornPlaceholder, absMediaUrl } from "@/components/AcornPlaceholder";

export default function AdminFeatured() {
    const [allWines, setAllWines] = useState([]);
    const [selected, setSelected] = useState([]);   // [{id, name, order}]
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [pick, setPick] = useState("");

    useEffect(() => {
        Promise.all([
            api.get("/admin/wines", { params: { limit: 200 } }),
            api.get("/wines/featured"),
        ]).then(([{ data: all }, { data: feat }]) => {
            setAllWines(all.items);
            const inFeat = (feat.items || []).map((w, i) => ({
                id: w.id, name: w.name, producer: w.producer, vintage: w.vintage,
                images: w.images, order: w.featured_order ?? (i + 1),
            })).sort((a, b) => a.order - b.order);
            setSelected(inFeat);
        }).finally(() => setLoading(false));
    }, []);

    const availableToAdd = allWines.filter((w) => !selected.some((s) => s.id === w.id));

    const addSel = () => {
        if (!pick) return;
        const w = allWines.find((x) => x.id === pick);
        if (!w) return;
        setSelected([...selected, {
            id: w.id, name: w.name, producer: w.producer, vintage: w.vintage,
            images: w.images, order: selected.length + 1,
        }]);
        setPick("");
    };

    const move = (idx, dir) => {
        const j = idx + dir;
        if (j < 0 || j >= selected.length) return;
        const next = [...selected];
        [next[idx], next[j]] = [next[j], next[idx]];
        setSelected(next.map((s, i) => ({ ...s, order: i + 1 })));
    };

    const remove = (idx) => {
        const next = selected.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 }));
        setSelected(next);
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/featured-wines", {
                items: selected.map((s, i) => ({ id: s.id, order: i + 1 })),
            });
            toast.success("Selezione salvata");
        } catch {
            toast.error("Errore salvataggio");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="text-[hsl(var(--muted-foreground))]">Caricamento…</div>;

    return (
        <div data-testid="admin-featured-page">
            <div className="mb-6 flex items-end justify-between">
                <div>
                    <h1 className="text-3xl">Selezione del Sommelier</h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                        {selected.length} etichett{selected.length === 1 ? "a" : "e"} in evidenza sulla home.
                    </p>
                </div>
                <button onClick={save} disabled={saving} className="co-btn-primary" data-testid="featured-save-btn">
                    {saving ? "Salvataggio…" : "Salva selezione"}
                </button>
            </div>

            <div className="bg-white border border-[hsl(var(--border))] rounded-sm p-4 mb-6 flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
                <div className="flex-1">
                    <label className="co-caption mb-2 block">Aggiungi un vino</label>
                    <Select value={pick} onValueChange={setPick}>
                        <SelectTrigger className="bg-white" data-testid="featured-add-select">
                            <SelectValue placeholder="Scegli un vino…" />
                        </SelectTrigger>
                        <SelectContent className="max-h-96">
                            {availableToAdd.map((w) => (
                                <SelectItem key={w.id} value={w.id}>
                                    {w.name} — {w.producer} {w.vintage || ""}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <button onClick={addSel} disabled={!pick} className="co-btn-ghost" data-testid="featured-add-btn">
                    <Plus className="w-4 h-4" /> Aggiungi
                </button>
            </div>

            <div className="space-y-2">
                {selected.map((w, idx) => {
                    const thumb = absMediaUrl(w.images?.thumb_url);
                    return (
                        <div key={w.id} className="bg-white border border-[hsl(var(--border))] rounded-sm p-3 flex items-center gap-4" data-testid={`featured-row-${w.id}`}>
                            <div className="w-8 co-caption text-center">{idx + 1}</div>
                            <div className="w-14 h-14 flex items-center justify-center bg-[hsl(var(--secondary))] rounded-sm shrink-0">
                                {thumb ? (
                                    <img src={thumb} alt="" className="max-h-full max-w-full object-contain" loading="lazy" />
                                ) : (
                                    <AcornPlaceholder size={36} opacity={0.55} />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-base truncate">{w.name} {w.vintage ? <span className="italic text-[hsl(var(--muted-foreground))]">{w.vintage}</span> : null}</div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{w.producer}</div>
                            </div>
                            <div className="flex gap-1">
                                <button className="p-2 hover:bg-[hsl(var(--secondary))] rounded-sm disabled:opacity-30" disabled={idx === 0} onClick={() => move(idx, -1)} data-testid={`featured-up-${w.id}`}>
                                    <ArrowUp className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                                <button className="p-2 hover:bg-[hsl(var(--secondary))] rounded-sm disabled:opacity-30" disabled={idx === selected.length - 1} onClick={() => move(idx, 1)} data-testid={`featured-down-${w.id}`}>
                                    <ArrowDown className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                                <button className="p-2 hover:bg-[hsl(var(--secondary))] text-[hsl(var(--destructive))] rounded-sm" onClick={() => remove(idx)} data-testid={`featured-remove-${w.id}`}>
                                    <X className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                            </div>
                        </div>
                    );
                })}
                {selected.length === 0 && (
                    <div className="text-[hsl(var(--muted-foreground))] italic text-center py-8">
                        Nessun vino selezionato — aggiungine per popolare la home.
                    </div>
                )}
            </div>
        </div>
    );
}
